import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CheckCircle2,
  FileCheck,
  Loader2,
  RefreshCw,
  Save,
  Sparkles,
  Square,
  Volume2,
} from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../hooks/use-toast";
import {
  getCondition,
  getConditionInstructions,
  getConditions,
  previewConditionInstructionBriefing,
  previewConditionInstructionVariants,
  saveConditionInstructionVariants,
  updateConditionInstructionBriefingScript,
  type InstructionVariantSelectionPayload,
} from "../../lib/admin-api";
import {
  compareConditionItems,
  normalizeConditionCategories,
  type ConditionCategoryItem,
  type NormalizedConditionCategory,
} from "../../lib/conditionTableNormalizer";
import {
  COURSE_CATEGORIES,
  conditionCategoryIdsForCourse,
  findCourseCategory,
  isCompositeCourseCategory,
} from "../../lib/course-categories";

type GuidanceLevel = "detailed" | "viewpoints" | "purpose_only";

interface EditableTask {
  no: number;
  title: string;
  details?: string[];
  speech?: string;
  related_conditions?: string[];
  source_cells?: string[];
}

interface EditableVariant {
  label: string;
  summary?: string;
  tasks: EditableTask[];
}

interface CourseConditionSnapshotCell {
  cellId: string;
  categoryId: string;
  categoryName: string;
  levelKey: string;
  label: string;
  text: string;
}

interface CourseConditionSnapshot {
  sourceConditionId: number;
  sourceConditionVersion: number;
  copiedFromInstructionId?: number;
  cells: CourseConditionSnapshotCell[];
}

interface EditableInstructionData extends Record<string, unknown> {
  summary?: string;
  mode?: string;
  course_category_id?: number;
  course_category_name?: string;
  tasks?: EditableTask[];
  target_categories?: string[];
  target_category_levels?: Record<string, string[]>;
  instruction_variants?: Partial<Record<GuidanceLevel, EditableVariant>>;
  selected_guidance_level?: GuidanceLevel;
  generated_guidance_levels?: GuidanceLevel[];
  lecture_title?: string;
  briefing_script?: string;
  source_instruction_id?: number;
  edit_owner_user_id?: number;
  course_condition_snapshot?: CourseConditionSnapshot;
}

interface LevelCell {
  levelKey: string;
  label: string;
  text: string;
  items: ConditionCategoryItem[];
}

const GUIDANCE_META: Record<
  GuidanceLevel,
  { short: string; description: string }
> = {
  detailed: {
    short: "高支援",
    description: "選択したセルを1セル1タスクで表示",
  },
  viewpoints: {
    short: "中支援",
    description: "カテゴリごとに最大3タスクへ整理",
  },
  purpose_only: { short: "低支援", description: "カテゴリごとに1タスクへ統合" },
};

const GUIDANCE_LEVELS = Object.keys(GUIDANCE_META) as GuidanceLevel[];

function displayLevelLabel(items: ConditionCategoryItem[]): string {
  const first = items[0];
  if (!first) return "";
  if (first.kind === "goal") return "歯科衛生士提案";
  if (first.kind === "commitment") return "患者受容・意思";
  return Array.from(
    new Set(items.map((item) => item.label.replace(/\s*[①-㊿]\s*$/u, ""))),
  ).join("・");
}

function cellsForCategory(category: NormalizedConditionCategory): LevelCell[] {
  const grouped = new Map<string, ConditionCategoryItem[]>();
  for (const item of category.items.slice().sort(compareConditionItems)) {
    if (!item.text.trim() || item.text.trim() === "N/A") continue;
    const items = grouped.get(item.level) ?? [];
    items.push(item);
    grouped.set(item.level, items);
  }
  return Array.from(grouped.entries()).map(([levelKey, items]) => ({
    levelKey,
    label: displayLevelLabel(items),
    text: items.map((item) => item.text).join(" / "),
    items,
  }));
}

function selectedKey(categoryId: string, levelKey: string): string {
  return `${categoryId}::${levelKey}`;
}

function canonicalCategoryId(categoryId: string): string {
  const normalized = categoryId.trim().toUpperCase();
  return normalized.startsWith("SRC_") ? normalized.slice(4) : normalized;
}

function targetLevelsForCategory(
  targetLevels: Record<string, string[]>,
  categoryId: string,
): string[] {
  return Array.from(
    new Set([
      ...(targetLevels[categoryId] ?? []),
      ...(targetLevels[canonicalCategoryId(categoryId)] ?? []),
    ]),
  );
}

function cloneInstruction(
  data: EditableInstructionData,
): EditableInstructionData {
  return JSON.parse(JSON.stringify(data)) as EditableInstructionData;
}

export default function InstructionVariantBuilder() {
  const initialSearchParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  const initialCourseCategory =
    initialSearchParams.get("courseCategory") ?? "";
  const initialConditionId = initialSearchParams.get("conditionId") ?? "";
  const requestedInstructionId = Number.parseInt(
    initialSearchParams.get("instructionId") ?? "",
    10,
  );
  const [courseCategoryId, setCourseCategoryId] = useState(
    initialCourseCategory,
  );
  const [conditionId, setConditionId] = useState(initialConditionId);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [generationLevels, setGenerationLevels] = useState<Set<GuidanceLevel>>(
    new Set(GUIDANCE_LEVELS),
  );
  const [instructionData, setInstructionData] =
    useState<EditableInstructionData | null>(null);
  const [activeLevel, setActiveLevel] = useState<GuidanceLevel>("detailed");
  const [savedInstructionId, setSavedInstructionId] = useState<number | null>(
    null,
  );
  const [savedReviewStatus, setSavedReviewStatus] = useState<
    "draft" | "in_review" | "approved" | null
  >(null);
  const [loadedRequestedInstructionId, setLoadedRequestedInstructionId] =
    useState<number | null>(null);
  const [briefingAudioState, setBriefingAudioState] = useState<
    "idle" | "loading" | "playing"
  >("idle");
  const briefingAudioRef = useRef<HTMLAudioElement | null>(null);
  const briefingAudioUrlRef = useRef<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isTeacher =
    typeof window !== "undefined" && window.location.pathname.startsWith("/teacher");

  const conditionsQuery = useQuery({
    queryKey: ["admin", "conditions", "instruction-variant-builder"],
    queryFn: () => getConditions({ limit: 500 }),
  });
  const conditions = conditionsQuery.data?.conditions ?? [];
  const selectedCourse = findCourseCategory(
    Number.parseInt(courseCategoryId, 10),
  );
  const isCopiedDraft = Boolean(instructionData?.source_instruction_id);
  const usesManualCategorySelection = selectedCourse
    ? isCompositeCourseCategory(selectedCourse.id)
    : false;
  const usesSavedCategorySelection =
    usesManualCategorySelection || isCopiedDraft;
  const automaticCategoryIds = useMemo(
    () =>
      new Set(
        selectedCourse
          ? conditionCategoryIdsForCourse(selectedCourse.id)
          : [],
      ),
    [selectedCourse],
  );
  const selectedId = conditionId ? Number.parseInt(conditionId, 10) : null;
  const conditionQuery = useQuery({
    queryKey: ["admin", "condition", selectedId],
    queryFn: () => getCondition(selectedId!),
    enabled: Boolean(selectedId),
  });
  const condition = conditionQuery.data?.condition ?? null;
  const instructionsQuery = useQuery({
    queryKey: ["admin", "condition-instructions", selectedId],
    queryFn: () => getConditionInstructions(selectedId!),
    enabled: Boolean(selectedId),
  });
  const latestPendingInstruction = useMemo(() => {
    const instructions = instructionsQuery.data?.instructions ?? [];
    return (
      instructions.find(
        (item: {
          id: number;
          review_status?: string;
          instruction_data?: EditableInstructionData;
        }) =>
          (item.review_status === "draft" ||
            item.review_status === "in_review") &&
          item.instruction_data?.instruction_variants,
      ) ?? null
    );
  }, [instructionsQuery.data]);
  const requestedInstruction = useMemo(() => {
    if (!Number.isFinite(requestedInstructionId)) return null;
    const instructions = instructionsQuery.data?.instructions ?? [];
    return (
      instructions.find(
        (item: {
          id: number;
          review_status?: "draft" | "in_review" | "approved";
          instruction_data?: EditableInstructionData;
        }) => item.id === requestedInstructionId && item.instruction_data,
      ) ?? null
    );
  }, [instructionsQuery.data, requestedInstructionId]);
  const categories = useMemo(
    () => normalizeConditionCategories(condition?.condition_data),
    [condition?.condition_data],
  );
  const categoryCells = useMemo(
    () =>
      new Map(
        categories.map((category) => [
          category.categoryId,
          cellsForCategory(category),
        ]),
      ),
    [categories],
  );

  const selectedLevelKeys = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const category of categories) {
      const levels = (categoryCells.get(category.categoryId) ?? [])
        .filter(
          (cell) =>
            (!usesSavedCategorySelection &&
              automaticCategoryIds.has(
                canonicalCategoryId(category.categoryId),
              )) ||
            (usesSavedCategorySelection &&
              selectedCells.has(
                selectedKey(category.categoryId, cell.levelKey),
              )),
        )
        .map((cell) => cell.levelKey);
      if (levels.length > 0) result[category.categoryId] = levels;
    }
    return result;
  }, [
    automaticCategoryIds,
    categories,
    categoryCells,
    selectedCells,
    usesSavedCategorySelection,
  ]);

  const automaticallySelectedCategories = useMemo(
    () =>
      categories.filter((category) =>
        automaticCategoryIds.has(canonicalCategoryId(category.categoryId)),
      ),
    [automaticCategoryIds, categories],
  );

  const selectedCategoryCount = Object.keys(selectedLevelKeys).length;
  const selectedCellCount = Object.values(selectedLevelKeys).reduce(
    (sum, levels) => sum + levels.length,
    0,
  );
  const expectedCounts = {
    detailed: selectedCellCount,
    viewpoints: Object.values(selectedLevelKeys).reduce(
      (sum, levels) => sum + Math.min(3, levels.length),
      0,
    ),
    purpose_only: selectedCategoryCount,
  };

  const buildPayload = (): InstructionVariantSelectionPayload => {
    const selected: InstructionVariantSelectionPayload["selected_cells"] = [];
    categories.forEach((category, rowIndex) => {
      const levels = selectedLevelKeys[category.categoryId];
      if (!levels?.length) return;
      selected.push({
        rowIndex,
        colIndex: 0,
        header: "ID",
        value: category.categoryId,
      });
      (categoryCells.get(category.categoryId) ?? []).forEach(
        (cell, cellIndex) => {
          if (!levels.includes(cell.levelKey)) return;
          selected.push({
            rowIndex,
            colIndex: cellIndex + 1,
            header: cell.levelKey,
            value: cell.text,
          });
        },
      );
    });
    return {
      selected_cells: selected,
      selected_level_keys: selectedLevelKeys,
      guidance_levels: GUIDANCE_LEVELS.filter((level) =>
        generationLevels.has(level),
      ),
    };
  };

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!selectedCourse)
        throw new Error("先に講座カテゴリを選択してください。");
      if (!selectedId) throw new Error("健康心理条件を選択してください。");
      return previewConditionInstructionVariants(selectedId, buildPayload());
    },
    onSuccess: (data) => {
      setInstructionData((current) => ({
        ...cloneInstruction(data.instruction_data),
        ...(current?.course_condition_snapshot
          ? { course_condition_snapshot: current.course_condition_snapshot }
          : {}),
        ...(current?.source_instruction_id
          ? { source_instruction_id: current.source_instruction_id }
          : {}),
        ...(current?.edit_owner_user_id
          ? { edit_owner_user_id: current.edit_owner_user_id }
          : {}),
      }));
      setSavedInstructionId(null);
      setSavedReviewStatus(null);
      const generated = data.instruction_data.generated_guidance_levels as
        GuidanceLevel[] | undefined;
      const selected = data.instruction_data.selected_guidance_level as
        GuidanceLevel | undefined;
      setActiveLevel(selected ?? generated?.[0] ?? "viewpoints");
      toast({
        title: `${generated?.length ?? generationLevels.size}種類の課題文を生成しました`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "課題文を生成できませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const briefingMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId || !instructionData)
        throw new Error("先に課題文を生成してください。");
      return previewConditionInstructionBriefing(selectedId, {
        instruction_data: instructionData as Record<string, unknown>,
        ...(instructionData.selected_guidance_level
          ? { guidance_level: instructionData.selected_guidance_level }
          : {}),
      });
    },
    onSuccess: (data) => {
      const script =
        typeof data.briefing_script === "string" ? data.briefing_script : "";
      setInstructionData((current) =>
        current ? { ...current, briefing_script: script } : current,
      );
      toast({ title: "読み上げ用の説明文を生成しました" });
    },
    onError: (error: Error) => {
      toast({
        title: "読み上げ用の説明文を生成できませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const briefingSaveMutation = useMutation({
    mutationFn: async () => {
      if (!savedInstructionId || !instructionData) {
        throw new Error("先に課題文を保存してください。");
      }
      return updateConditionInstructionBriefingScript(
        savedInstructionId,
        instructionData.briefing_script ?? "",
      );
    },
    onSuccess: (data) => {
      const savedScript =
        typeof data?.instruction?.instruction_data?.briefing_script ===
        "string"
          ? data.instruction.instruction_data.briefing_script
          : "";
      setInstructionData((current) =>
        current ? { ...current, briefing_script: savedScript } : current,
      );
      queryClient.invalidateQueries({
        queryKey: ["admin", "all-condition-instructions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "condition-instructions", selectedId],
      });
      toast({ title: "読み上げ文だけを保存しました" });
    },
    onError: (error: Error) => {
      toast({
        title: "読み上げ文を保存できませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const stopBriefingAudio = () => {
    const audio = briefingAudioRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
    }
    briefingAudioRef.current = null;
    if (briefingAudioUrlRef.current) {
      URL.revokeObjectURL(briefingAudioUrlRef.current);
      briefingAudioUrlRef.current = null;
    }
    setBriefingAudioState("idle");
  };

  const playBriefingAudio = async () => {
    const text = (instructionData?.briefing_script ?? "").trim();
    if (!text) return;
    stopBriefingAudio();
    setBriefingAudioState("loading");
    try {
      const response = await fetch("/api/generate-audio-sample", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text, gender: "Female" }),
      });
      if (!response.ok) throw new Error("音声を生成できませんでした");
      const url = URL.createObjectURL(await response.blob());
      briefingAudioUrlRef.current = url;
      const audio = new Audio(url);
      briefingAudioRef.current = audio;
      audio.onended = () => stopBriefingAudio();
      audio.onerror = () => stopBriefingAudio();
      await audio.play();
      setBriefingAudioState("playing");
    } catch (error) {
      stopBriefingAudio();
      toast({
        title: "試聴できませんでした",
        description: (error as Error).message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => stopBriefingAudio, []);

  const saveMutation = useMutation({
    mutationFn: async (reviewStatus: "in_review" | "approved") => {
      if (!selectedCourse) throw new Error("講座カテゴリを選択してください。");
      if (!selectedId || !instructionData)
        throw new Error("保存する課題文がありません。");
      if (!instructionData.selected_guidance_level)
        throw new Error("保存して読み上げる課題文を選択してください。");
      const selectedGuidanceLevel = instructionData.selected_guidance_level;
      const prepared = cloneInstruction(instructionData);
      const lectureTitle = (prepared.lecture_title ?? "").trim();
      if (lectureTitle) prepared.lecture_title = lectureTitle;
      else delete prepared.lecture_title;
      const briefingScript = (prepared.briefing_script ?? "").trim();
      if (briefingScript) prepared.briefing_script = briefingScript;
      else delete prepared.briefing_script;
      prepared.course_category_id = selectedCourse.id;
      prepared.course_category_name = selectedCourse.name;
      if (selectedCourse.id === 1) prepared.mode = "intake";
      const selectedVariant =
        prepared.instruction_variants?.[selectedGuidanceLevel];
      prepared.tasks = selectedVariant?.tasks ?? [];
      return saveConditionInstructionVariants(selectedId, {
        ...buildPayload(),
        instruction_data: prepared,
        selected_guidance_level: selectedGuidanceLevel,
        review_status: reviewStatus,
        ...(savedInstructionId ? { instruction_id: savedInstructionId } : {}),
      });
    },
    onSuccess: (data, reviewStatus) => {
      const id = data.instruction?.id as number | undefined;
      if (id) setSavedInstructionId(id);
      setSavedReviewStatus(reviewStatus);
      queryClient.invalidateQueries({
        queryKey: ["admin", "all-condition-instructions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "condition-instructions", selectedId],
      });
      toast({
        title:
          reviewStatus === "approved"
            ? `課題文 No.${id ?? savedInstructionId ?? ""} を承認して保存しました`
            : isTeacher
              ? "管理者へ確認を依頼しました"
            : savedInstructionId
              ? "レビュー待ちの課題文を更新しました"
              : `課題文 No.${id ?? ""} をレビュー待ちで保存しました`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "課題文を保存できませんでした",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetGenerated = () => {
    setInstructionData(null);
    setSavedInstructionId(null);
    setSavedReviewStatus(null);
  };

  const toggleGenerationLevel = (level: GuidanceLevel) => {
    resetGenerated();
    setGenerationLevels((current) => {
      const next = new Set(current);
      if (next.has(level)) {
        if (next.size === 1) return current;
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  const toggleCell = (categoryId: string, levelKey: string) => {
    resetGenerated();
    setSelectedCells((current) => {
      const next = new Set(current);
      const key = selectedKey(categoryId, levelKey);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCategory = (category: NormalizedConditionCategory) => {
    resetGenerated();
    const cells = categoryCells.get(category.categoryId) ?? [];
    const allSelected =
      cells.length > 0 &&
      cells.every((cell) =>
        selectedCells.has(selectedKey(category.categoryId, cell.levelKey)),
      );
    setSelectedCells((current) => {
      const next = new Set(current);
      cells.forEach((cell) => {
        const key = selectedKey(category.categoryId, cell.levelKey);
        if (allSelected) next.delete(key);
        else next.add(key);
      });
      return next;
    });
  };

  const updateVariant = (
    level: GuidanceLevel,
    updater: (variant: EditableVariant) => EditableVariant,
  ) => {
    setInstructionData((current) => {
      if (!current?.instruction_variants?.[level]) return current;
      const next = cloneInstruction(current);
      const variant = next.instruction_variants?.[level];
      if (!variant || !next.instruction_variants) return current;
      next.instruction_variants[level] = updater(variant);
      return next;
    });
  };

  const updateCourseConditionCell = (cellId: string, text: string) => {
    setInstructionData((current) => {
      if (!current?.course_condition_snapshot) return current;
      return {
        ...current,
        course_condition_snapshot: {
          ...current.course_condition_snapshot,
          cells: current.course_condition_snapshot.cells.map((cell) =>
            cell.cellId === cellId ? { ...cell, text } : cell,
          ),
        },
      };
    });
  };

  const resumeSavedInstruction = (item: {
    id: number;
    review_status?: "draft" | "in_review" | "approved";
    instruction_data: EditableInstructionData;
  }) => {
    const targetLevels = item.instruction_data.target_category_levels ?? {};
    const restored = new Set<string>();
    for (const category of categories) {
      for (const levelKey of targetLevelsForCategory(
        targetLevels,
        category.categoryId,
      )) {
        restored.add(selectedKey(category.categoryId, levelKey));
      }
    }
    setSelectedCells(restored);
    const restoredGuidanceLevels =
      item.instruction_data.generated_guidance_levels ??
      GUIDANCE_LEVELS.filter((level) =>
        Boolean(item.instruction_data.instruction_variants?.[level]),
      );
    const restoredSelectedGuidanceLevel =
      item.instruction_data.selected_guidance_level ??
      (restoredGuidanceLevels.includes("viewpoints")
        ? "viewpoints"
        : restoredGuidanceLevels[0]);
    setInstructionData({
      ...cloneInstruction(item.instruction_data),
      generated_guidance_levels: restoredGuidanceLevels,
      selected_guidance_level: restoredSelectedGuidanceLevel,
    });
    setGenerationLevels(new Set(restoredGuidanceLevels));
    setSavedInstructionId(item.id);
    setSavedReviewStatus(item.review_status ?? "draft");
    setActiveLevel(restoredSelectedGuidanceLevel ?? "viewpoints");
  };

  useEffect(() => {
    const item = requestedInstruction;
    if (
      !item?.instruction_data ||
      loadedRequestedInstructionId === item.id ||
      categories.length === 0
    ) {
      return;
    }
    const targetLevels = item.instruction_data.target_category_levels ?? {};
    const restored = new Set<string>();
    for (const category of categories) {
      for (const levelKey of targetLevelsForCategory(
        targetLevels,
        category.categoryId,
      )) {
        restored.add(selectedKey(category.categoryId, levelKey));
      }
    }
    const restoredGuidanceLevels =
      item.instruction_data.generated_guidance_levels ??
      GUIDANCE_LEVELS.filter((level) =>
        Boolean(item.instruction_data?.instruction_variants?.[level]),
      );
    const restoredSelectedGuidanceLevel =
      item.instruction_data.selected_guidance_level ??
      (restoredGuidanceLevels.includes("viewpoints")
        ? "viewpoints"
        : restoredGuidanceLevels[0]);
    setSelectedCells(restored);
    setInstructionData({
      ...cloneInstruction(item.instruction_data),
      generated_guidance_levels: restoredGuidanceLevels,
      selected_guidance_level: restoredSelectedGuidanceLevel,
    });
    setGenerationLevels(new Set(restoredGuidanceLevels));
    setSavedInstructionId(item.id);
    setSavedReviewStatus(item.review_status ?? "draft");
    setActiveLevel(restoredSelectedGuidanceLevel ?? "viewpoints");
    setLoadedRequestedInstructionId(item.id);
  }, [categories, loadedRequestedInstructionId, requestedInstruction]);

  const currentVariant =
    instructionData?.instruction_variants?.[activeLevel] ?? null;
  return (
    <AdminLayout>
      <div className="mx-auto max-w-[1500px] space-y-6 pb-12">
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-gray-200 pb-5">
          <div>
            <div className="mb-2 text-sm font-bold text-cyan-700">
              課題文作成
            </div>
            <h1 className="flex items-center gap-3 text-3xl font-bold text-gray-950">
              <FileCheck className="h-8 w-8" />
              支援度別の課題文を作成
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              講座カテゴリと健康心理条件を決め、必要な支援度だけ課題文を生成します。
            </p>
          </div>
          {savedInstructionId && (
            <div
              className={`rounded-md border px-4 py-2 text-sm font-bold ${savedReviewStatus === "approved" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}
            >
              {savedReviewStatus === "approved" ? "承認済み" : "レビュー待ち"}:
              No.{savedInstructionId}
            </div>
          )}
        </div>

        <section className="border-b border-gray-200 pb-6">
          <div className="mb-3 flex items-center gap-3">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-gray-950 text-sm font-bold text-white">
              1
            </span>
            <div>
              <h2 className="text-xl font-bold text-gray-950">
                学生が選ぶ講座を指定
              </h2>
              <p className="text-sm text-gray-600">
                保存した課題文を、学生画面の13講座のどこに表示するか決めます。
              </p>
            </div>
          </div>
          <Select
            value={courseCategoryId}
            disabled={isCopiedDraft}
            onValueChange={(value) => {
              setCourseCategoryId(value);
              setConditionId("");
              setSelectedCells(new Set());
              resetGenerated();
            }}
          >
            <SelectTrigger className="h-12 w-full max-w-2xl rounded-md border-gray-300 bg-white text-base">
              <SelectValue placeholder="1〜13の講座カテゴリを選択してください" />
            </SelectTrigger>
            <SelectContent>
              {COURSE_CATEGORIES.map((category) => (
                <SelectItem key={category.id} value={String(category.id)}>
                  {category.id}．{category.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCourse?.id === 1 && (
            <div className="mt-3 max-w-2xl rounded-md border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-950">
              「初診時医療面接」として保存します。承認後、学生画面の「1．初診時医療面接」で使用できます。
            </div>
          )}
        </section>

        <section className="border-b border-gray-200 pb-6">
          <div className="mb-3 flex items-center gap-3">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-gray-950 text-sm font-bold text-white">
              2
            </span>
            <h2 className="text-xl font-bold text-gray-950">
              健康心理条件を選択
            </h2>
          </div>
          <Select
            value={conditionId}
            disabled={!selectedCourse || isCopiedDraft}
            onValueChange={(value) => {
              setConditionId(value);
              setSelectedCells(new Set());
              resetGenerated();
            }}
          >
            <SelectTrigger className="h-12 w-full max-w-xl rounded-md border-gray-300 bg-white text-base">
              <SelectValue
                placeholder={
                  selectedCourse
                    ? "健康心理条件IDを選択してください"
                    : "先に講座カテゴリを選択してください"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {conditions.map(
                (item: { id: number; gender?: string; age_range?: string }) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    ID {item.id}　{item.gender ?? "-"}・{item.age_range ?? "-"}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
          {condition && (
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
              <span>
                健康心理条件ID:{" "}
                <strong className="text-gray-950">{condition.id}</strong>
              </span>
              <span>
                性別:{" "}
                <strong className="text-gray-950">
                  {condition.gender ?? "-"}
                </strong>
              </span>
              <span>
                年代:{" "}
                <strong className="text-gray-950">
                  {condition.age_range ?? "-"}
                </strong>
              </span>
            </div>
          )}
          {latestPendingInstruction && !instructionData && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3">
              <div className="text-sm text-amber-950">
                <strong>
                  レビュー待ちの課題文 No.{latestPendingInstruction.id}
                </strong>{" "}
                があります。候補を読み込み、内容を確認してから保存できます。
              </div>
              <Button
                type="button"
                onClick={() => resumeSavedInstruction(latestPendingInstruction)}
                variant="outline"
                className="font-bold"
              >
                内容を読み込んでレビュー
              </Button>
            </div>
          )}
        </section>

        <section className="border-b border-gray-200 pb-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-gray-950 text-sm font-bold text-white">
              3
            </span>
            <div>
              <h2 className="text-xl font-bold text-gray-950">
                生成する支援度を選択
              </h2>
              <p className="text-sm text-gray-600">
                比較したい支援度を選びます。1種類だけでも生成できます。
              </p>
            </div>
          </div>
          <div className="grid max-w-4xl gap-3 sm:grid-cols-3">
            {GUIDANCE_LEVELS.map((level) => {
              const meta = GUIDANCE_META[level];
              const checked = generationLevels.has(level);
              return (
                <button
                  key={level}
                  type="button"
                  aria-pressed={checked}
                  disabled={isCopiedDraft}
                  onClick={() => toggleGenerationLevel(level)}
                  className={`min-h-[92px] rounded-md border p-4 text-left transition-colors ${
                    checked
                      ? "border-cyan-700 bg-cyan-50 text-cyan-950"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-400"
                  }`}
                >
                  <span className="flex items-center justify-between gap-3">
                    <span className="font-bold">{meta.short}</span>
                    <span
                      className={`flex h-6 w-10 items-center rounded-full p-0.5 ${checked ? "justify-end bg-cyan-700" : "justify-start bg-gray-300"}`}
                    >
                      <span className="block h-5 w-5 rounded-full bg-white shadow-sm" />
                    </span>
                  </span>
                  <span className="mt-2 block text-xs leading-5 text-gray-600">
                    {meta.description}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="border-b border-gray-200 pb-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="grid h-7 w-7 place-items-center rounded-full bg-gray-950 text-sm font-bold text-white">
                4
              </span>
              <div>
                <h2 className="text-xl font-bold text-gray-950">
                  {usesManualCategorySelection
                    ? "使用するカテゴリを選択"
                    : "講座に対応するカテゴリ"}
                </h2>
                <p className="text-sm text-gray-600">
                  {usesManualCategorySelection
                    ? "複合アバターで使用する項目だけを選びます。"
                    : "この講座に必要な項目は、すべて自動で選択されます。"}
                </p>
              </div>
            </div>
            {selectedCellCount > 0 && (
              <div className="flex flex-wrap gap-2 text-xs font-bold">
                <span className="rounded-full bg-cyan-50 px-3 py-1.5 text-cyan-800">
                  高支援 {expectedCounts.detailed}タスク
                </span>
                <span className="rounded-full bg-blue-50 px-3 py-1.5 text-blue-800">
                  中支援 {expectedCounts.viewpoints}タスク
                </span>
                <span className="rounded-full bg-gray-100 px-3 py-1.5 text-gray-700">
                  低支援 {expectedCounts.purpose_only}タスク
                </span>
              </div>
            )}
          </div>

          {!selectedId ? (
            <div className="border border-dashed border-gray-300 py-12 text-center text-sm text-gray-500">
              先に健康心理条件IDを選択してください。
            </div>
          ) : conditionQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-7 w-7 animate-spin" />
            </div>
          ) : categories.length === 0 ? (
            <div className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
              この健康心理条件には選択できるルーブリックがありません。
            </div>
          ) : !usesManualCategorySelection ? (
            automaticallySelectedCategories.length > 0 ? (
              <div className="rounded-md border border-cyan-200 bg-cyan-50/70 px-4 py-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-cyan-700" />
                  <div>
                    <div className="font-bold text-cyan-950">
                      {automaticallySelectedCategories
                        .map((category) => category.categoryName)
                        .join("・")}
                    </div>
                    <div className="mt-1 text-sm text-cyan-900">
                      {isCopiedDraft
                        ? "対応カテゴリを自動設定しています。公開版の課題文構成はそのまま引き継ぎます。"
                        : `入力済みの${selectedCellCount}項目を選択済みです。個別のチェック操作は必要ありません。`}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                この健康心理条件には、講座「{selectedCourse?.name}」に対応するカテゴリがありません。
              </div>
            )
          ) : (
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {categories.map((category) => {
                const cells = categoryCells.get(category.categoryId) ?? [];
                const selectedCount = cells.filter((cell) =>
                  selectedCells.has(
                    selectedKey(category.categoryId, cell.levelKey),
                  ),
                ).length;
                const allSelected =
                  cells.length > 0 && selectedCount === cells.length;
                return (
                  <div
                    key={category.categoryId}
                    className="grid gap-3 py-4 lg:grid-cols-[250px_1fr]"
                  >
                    <label className="flex cursor-pointer items-start gap-3 px-1">
                      <Checkbox
                        checked={allSelected}
                        disabled={isCopiedDraft}
                        onCheckedChange={() => toggleCategory(category)}
                      />
                      <span>
                        <span className="block text-xs font-bold text-gray-500">
                          {category.categoryId}
                        </span>
                        <span className="block text-sm font-bold text-gray-950">
                          {category.categoryName}
                        </span>
                        <span className="mt-1 block text-xs text-gray-500">
                          {selectedCount}/{cells.length}セル選択
                        </span>
                      </span>
                    </label>
                    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                      {cells.map((cell) => {
                        const checked = selectedCells.has(
                          selectedKey(category.categoryId, cell.levelKey),
                        );
                        return (
                          <label
                            key={cell.levelKey}
                            className={`flex min-h-[92px] cursor-pointer gap-3 rounded-md border p-3 transition-colors ${
                              checked
                                ? "border-cyan-600 bg-cyan-50"
                                : "border-gray-200 bg-white hover:border-gray-400"
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              disabled={isCopiedDraft}
                              onCheckedChange={() =>
                                toggleCell(category.categoryId, cell.levelKey)
                              }
                            />
                            <span className="min-w-0">
                              <span className="block text-xs font-bold text-cyan-800">
                                {cell.label}
                              </span>
                              <span className="mt-1 line-clamp-3 block text-xs leading-5 text-gray-700">
                                {cell.text}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-5 flex justify-end">
            <Button
              onClick={() => generateMutation.mutate()}
              disabled={
                selectedCellCount === 0 ||
                generateMutation.isPending ||
                isCopiedDraft
              }
              className="h-12 bg-orange-500 px-6 font-bold text-white shadow-[0_0_0_4px_rgba(249,115,22,0.13),0_8px_22px_rgba(249,115,22,0.25)] hover:bg-orange-600"
            >
              {generateMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              選んだ支援度の課題文を生成
            </Button>
          </div>
        </section>

        <section>
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-gray-950 text-sm font-bold text-white">
              5
            </span>
            <div>
              <h2 className="text-xl font-bold text-gray-950">
                レビュー・編集・保存
              </h2>
              <p className="text-sm text-gray-600">
                文章を確認し、最後に学生へ提示する1案を決めます。
              </p>
            </div>
          </div>

          {!instructionData ? (
            <div className="border border-dashed border-gray-300 py-14 text-center text-sm text-gray-500">
              セルと支援度を選択して生成すると、ここに候補が表示されます。
            </div>
          ) : (
            <div>
              <div className="mb-6 rounded-md border border-cyan-200 bg-cyan-50/60 p-4">
                <label className="mb-2 block text-sm font-bold text-cyan-950">
                  シナリオの学習目標（全支援度共通）
                </label>
                <Textarea
                  value={instructionData.summary ?? ""}
                  onChange={(event) =>
                    setInstructionData((current) =>
                      current
                        ? { ...current, summary: event.target.value }
                        : current,
                    )
                  }
                  className="min-h-[88px] resize-y rounded-md border-cyan-300 bg-white"
                />
                <p className="mt-2 text-xs text-cyan-800">
                  選択画面にも表示されます。高・中・低支援で変わるのはタスクのヒント量だけです。
                </p>
              </div>

              <div className="mb-5 grid grid-cols-3 border-b border-gray-200">
                {GUIDANCE_LEVELS.filter((level) =>
                  Boolean(instructionData.instruction_variants?.[level]),
                ).map((level) => {
                  const meta = GUIDANCE_META[level];
                  const taskCount =
                    instructionData.instruction_variants?.[level]?.tasks
                      .length ?? 0;
                  const active = activeLevel === level;
                  return (
                    <button
                      key={level}
                      type="button"
                      onClick={() => setActiveLevel(level)}
                      className={`min-h-[76px] border-b-2 px-3 py-3 text-left ${active ? "border-cyan-700 bg-cyan-50" : "border-transparent hover:bg-gray-50"}`}
                    >
                      <span
                        className={`block text-sm font-bold ${active ? "text-cyan-900" : "text-gray-800"}`}
                      >
                        {meta.short} · {taskCount}タスク
                      </span>
                      <span className="mt-1 block text-xs text-gray-500">
                        {meta.description}
                      </span>
                    </button>
                  );
                })}
              </div>

              {currentVariant && (
                <div className="space-y-5">
                  <div className="divide-y divide-gray-200 border-y border-gray-200">
                    {currentVariant.tasks.map((task, taskIndex) => (
                      <div
                        key={`${activeLevel}-${taskIndex}`}
                        className="grid gap-4 py-5 lg:grid-cols-[56px_1fr_260px]"
                      >
                        <div className="grid h-9 w-9 place-items-center rounded-full bg-gray-950 text-sm font-bold text-white">
                          {taskIndex + 1}
                        </div>
                        <div className="space-y-3">
                          <div>
                            <label className="mb-1.5 block text-xs font-bold text-gray-600">
                              タスク本文
                            </label>
                            <Input
                              value={task.title}
                              onChange={(event) =>
                                updateVariant(activeLevel, (variant) => ({
                                  ...variant,
                                  tasks: variant.tasks.map((item, index) =>
                                    index === taskIndex
                                      ? { ...item, title: event.target.value }
                                      : item,
                                  ),
                                }))
                              }
                              className="h-11 rounded-md border-gray-300"
                            />
                          </div>
                          <div>
                            <label className="mb-1.5 block text-xs font-bold text-gray-600">
                              確認項目（詳細）
                            </label>
                            <Textarea
                              value={task.speech ?? ""}
                              placeholder="例：まず、患者さんに来院された理由と、いま最も困っていることを尋ねてください。"
                              onChange={(event) =>
                                updateVariant(activeLevel, (variant) => ({
                                  ...variant,
                                  tasks: variant.tasks.map((item, index) =>
                                    index === taskIndex
                                      ? { ...item, speech: event.target.value }
                                      : item,
                                  ),
                                }))
                              }
                              className="min-h-[88px] resize-y rounded-md border-gray-300"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              自動生成されます。実際に話しかける自然な文章へ修正できます。
                            </p>
                          </div>
                        </div>
                        <div className="rounded-md bg-gray-50 p-3">
                          <div className="mb-2 flex items-center gap-1.5 text-xs font-bold text-gray-700">
                            <Check className="h-3.5 w-3.5" />
                            元セル
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {(task.source_cells ?? []).map((cell) => (
                              <span
                                key={cell}
                                className="rounded bg-white px-2 py-1 text-xs font-bold text-gray-600 ring-1 ring-gray-200"
                              >
                                {cell}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {instructionData.course_condition_snapshot && (
                    <div className="rounded-md border border-cyan-200 bg-cyan-50/60 p-5">
                      <div className="mb-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-bold text-gray-950">
                            この講座で使う健康心理条件
                          </h3>
                          {instructionData.source_instruction_id && (
                            <span className="rounded-full bg-white px-2 py-1 text-xs font-bold text-cyan-800 ring-1 ring-cyan-200">
                              公開版 No.{instructionData.source_instruction_id} のコピー
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm leading-6 text-gray-600">
                          課題文で選ばれた項目だけを固定しています。ここで直した患者設定は、この改善案が承認された後の面接だけで使用されます。
                        </p>
                      </div>
                      <div className="space-y-3">
                        {instructionData.course_condition_snapshot.cells.map(
                          (cell) => (
                            <div
                              key={cell.cellId}
                              className="rounded-md border border-gray-200 bg-white p-4"
                            >
                              <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className="text-xs font-bold text-cyan-800">
                                  {cell.categoryId}・{cell.categoryName}
                                </span>
                                <span className="text-xs font-bold text-gray-500">
                                  {cell.label}
                                </span>
                              </div>
                              <Textarea
                                value={cell.text}
                                maxLength={2000}
                                onChange={(event) =>
                                  updateCourseConditionCell(
                                    cell.cellId,
                                    event.target.value,
                                  )
                                }
                                className="min-h-[88px] resize-y rounded-md border-gray-300 bg-white"
                              />
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                  <div className="rounded-md border border-gray-300 bg-white p-5">
                    <div className="mb-3">
                      <h3 className="text-base font-bold text-gray-950">
                        講座タイトル
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        学生画面の講座カードと「次のおすすめ面接」で、「講座N」の代わりに見出しとして表示されます。空欄のままなら従来どおり講座番号を表示します。
                      </p>
                    </div>
                    <Input
                      value={instructionData.lecture_title ?? ""}
                      placeholder="例：来院理由と困りごとを聞き取る"
                      maxLength={40}
                      onChange={(event) =>
                        setInstructionData((current) =>
                          current
                            ? { ...current, lecture_title: event.target.value }
                            : current,
                        )
                      }
                      className="h-11 rounded-md border-gray-300"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      40文字まで入力できます。学生が一覧で見分けられる短い名前にしてください。
                    </p>
                  </div>

                  <div className="rounded-md border border-gray-300 bg-white p-5">
                    <div className="mb-3">
                      <h3 className="text-base font-bold text-gray-950">
                        学生に表示する課題文を選択
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        ここで選んだ1案だけを、患者面接中に学生が読み返せる課題文として表示します。歯科医師が話す文章は、この下の「面接前に読み上げる説明文」で別に管理します。
                      </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-3">
                      {GUIDANCE_LEVELS.filter((level) =>
                        Boolean(instructionData.instruction_variants?.[level]),
                      ).map((level) => {
                        const selected =
                          instructionData.selected_guidance_level === level;
                        return (
                          <button
                            key={level}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => {
                              setInstructionData((current) =>
                                current
                                  ? {
                                      ...current,
                                      selected_guidance_level: level,
                                    }
                                  : current,
                              );
                              setActiveLevel(level);
                            }}
                            className={`rounded-md border px-4 py-3 text-left ${selected ? "border-orange-500 bg-orange-50" : "border-gray-200 hover:border-gray-400"}`}
                          >
                            <span className="flex items-center gap-2 text-sm font-bold text-gray-950">
                              <span
                                className={`grid h-5 w-5 place-items-center rounded-full border ${selected ? "border-orange-500 bg-orange-500 text-white" : "border-gray-400"}`}
                              >
                                {selected && <Check className="h-3.5 w-3.5" />}
                              </span>
                              {GUIDANCE_META[level].short}
                            </span>
                            <span className="mt-1 block pl-7 text-xs text-gray-500">
                              {GUIDANCE_META[level].description}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="rounded-md border border-gray-300 bg-white p-5">
                    <div className="mb-3">
                      <h3 className="text-base font-bold text-gray-950">
                        面接前に読み上げる説明文
                      </h3>
                      <p className="mt-1 text-sm text-gray-600">
                        課題文とは別に、歯科医師アバターが面接前へ一度だけ話す文章です。課題文を生成すると、選んだ支援度の課題から自動で一緒に作られます。ここだけ作り直すこともできます。
                      </p>
                    </div>
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                      <Button
                        variant="outline"
                        onClick={() => briefingMutation.mutate()}
                        disabled={briefingMutation.isPending}
                      >
                        {briefingMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="mr-2 h-4 w-4" />
                        )}
                        {instructionData.briefing_script?.trim()
                          ? "作り直す"
                          : "生成する"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={
                          briefingAudioState === "playing"
                            ? stopBriefingAudio
                            : playBriefingAudio
                        }
                        disabled={
                          !instructionData.briefing_script?.trim() ||
                          briefingAudioState === "loading"
                        }
                      >
                        {briefingAudioState === "loading" ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : briefingAudioState === "playing" ? (
                          <Square className="mr-2 h-4 w-4" />
                        ) : (
                          <Volume2 className="mr-2 h-4 w-4" />
                        )}
                        {briefingAudioState === "playing" ? "停止" : "試聴"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => briefingSaveMutation.mutate()}
                        disabled={
                          !savedInstructionId || briefingSaveMutation.isPending
                        }
                      >
                        {briefingSaveMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Save className="mr-2 h-4 w-4" />
                        )}
                        読み上げ文だけ保存
                      </Button>
                      <span className="text-xs font-bold tabular-nums text-gray-500">
                        {(instructionData.briefing_script ?? "").trim().length}
                        文字
                      </span>
                    </div>
                    <Textarea
                      value={instructionData.briefing_script ?? ""}
                      maxLength={400}
                      placeholder="課題文を生成すると自動で作られます。空のときは「生成する」を押してください。作成後はここで自由に直せます。"
                      onChange={(event) =>
                        setInstructionData((current) =>
                          current
                            ? { ...current, briefing_script: event.target.value }
                            : current,
                        )
                      }
                      className="min-h-[132px] resize-y rounded-md border-gray-300"
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      自動生成は60〜80文字が目安です。保存済みの課題文なら、セルを選び直さず「読み上げ文だけ保存」で更新できます。空欄で保存すると、従来どおり課題ごとの読み上げになります。
                    </p>
                  </div>

                  <div className="flex flex-wrap justify-end gap-3 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => generateMutation.mutate()}
                      disabled={generateMutation.isPending || isCopiedDraft}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      再生成
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => saveMutation.mutate("in_review")}
                      disabled={
                        saveMutation.isPending ||
                        !instructionData.selected_guidance_level
                      }
                      className="px-6 font-bold"
                    >
                      {saveMutation.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="mr-2 h-4 w-4" />
                      )}
                      {isTeacher ? "管理者へ確認依頼" : "レビュー待ちで保存"}
                    </Button>
                    {!isTeacher && (
                      <Button
                        onClick={() => saveMutation.mutate("approved")}
                        disabled={
                          saveMutation.isPending ||
                          !instructionData.selected_guidance_level
                        }
                        className="bg-orange-500 px-6 font-bold text-white shadow-[0_0_0_4px_rgba(249,115,22,0.13),0_8px_22px_rgba(249,115,22,0.25)] hover:bg-orange-600"
                      >
                        {saveMutation.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                        )}
                        承認して保存
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
